/**
 * MarkdownWithVisuals
 *
 * Markdown renderer with embedded illustration and chart support.
 * Parses {{illustration:Name}} and {{chart:Name}} markers and renders
 * corresponding visual components.
 *
 * Based on the existing inline markdown parsing pattern but with
 * visual component integration.
 */

import { Text, Link as PolarisLink, Box, BlockStack } from '@shopify/polaris';
import { HelpIllustrationEmbed } from './HelpIllustrationEmbed';
import { HelpChartEmbed } from './HelpChartEmbed';
import { isIllustrationName } from './illustrations';
import { isChartName } from './charts';

interface MarkdownWithVisualsProps {
  /** Raw markdown content with optional visual markers */
  content: string;
  /** Base path for relative links (prepended to maintain query params) */
  basePath?: string;
}

interface VisualMarker {
  type: 'illustration' | 'chart';
  name: string;
  caption?: string;
}

/** Parse visual markers from a line */
function parseVisualMarker(line: string): VisualMarker | null {
  // Match {{illustration:Name}} or {{illustration:Name|Caption text}}
  const illustrationMatch = line.match(
    /^\s*\{\{illustration:(\w+)(?:\|([^}]+))?\}\}\s*$/
  );
  if (illustrationMatch) {
    return {
      type: 'illustration',
      name: illustrationMatch[1],
      caption: illustrationMatch[2]?.trim(),
    };
  }

  // Match {{chart:Name}} or {{chart:Name|Caption text}}
  const chartMatch = line.match(/^\s*\{\{chart:(\w+)(?:\|([^}]+))?\}\}\s*$/);
  if (chartMatch) {
    return {
      type: 'chart',
      name: chartMatch[1],
      caption: chartMatch[2]?.trim(),
    };
  }

  return null;
}

/** Parse inline formatting (bold, italic, code, links) */
function parseInlineFormatting(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let keyIndex = 0;

  while (remaining.length > 0) {
    // Check for inline code `code`
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      parts.push(
        <code
          key={keyIndex++}
          style={{
            backgroundColor: '#F6F6F7',
            padding: '2px 6px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '0.9em',
          }}
        >
          {codeMatch[1]}
        </code>
      );
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    // Check for bold **text**
    const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
    if (boldMatch) {
      parts.push(
        <Text key={keyIndex++} as="span" fontWeight="semibold">
          {boldMatch[1]}
        </Text>
      );
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    // Check for italic *text* (but not **)
    const italicMatch = remaining.match(/^\*([^*]+)\*/);
    if (italicMatch && !remaining.startsWith('**')) {
      parts.push(
        <em key={keyIndex++}>{italicMatch[1]}</em>
      );
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    // Check for links [text](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      const linkText = linkMatch[1];
      const linkUrl = linkMatch[2];
      const isExternal = linkUrl.startsWith('http');
      parts.push(
        <PolarisLink
          key={keyIndex++}
          url={linkUrl}
          target={isExternal ? '_blank' : undefined}
          removeUnderline={false}
        >
          {linkText}
        </PolarisLink>
      );
      remaining = remaining.slice(linkMatch[0].length);
      continue;
    }

    // No match - take next character as plain text
    const plainMatch = remaining.match(/^[^`*[\]]+/);
    if (plainMatch) {
      parts.push(plainMatch[0]);
      remaining = remaining.slice(plainMatch[0].length);
    } else {
      // Take single character if no other match
      parts.push(remaining[0]);
      remaining = remaining.slice(1);
    }
  }

  return parts.length === 1 && typeof parts[0] === 'string' ? (
    parts[0]
  ) : (
    <>{parts}</>
  );
}

export function MarkdownWithVisuals({
  content,
  basePath,
}: MarkdownWithVisualsProps) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let inCodeBlock = false;
  let codeContent: string[] = [];
  let codeLanguage = '';
  let inList = false;
  let listItems: React.ReactNode[] = [];
  let listType: 'ul' | 'ol' = 'ul';
  let inTable = false;
  let tableRows: string[][] = [];
  let tableHeader: string[] = [];

  const flushCodeBlock = () => {
    if (codeContent.length > 0) {
      elements.push(
        <Box
          key={`code-${elements.length}`}
          background="bg-surface-secondary"
          padding="400"
          borderRadius="200"
        >
          <pre
            style={{
              margin: 0,
              fontFamily:
                'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
              fontSize: '13px',
              lineHeight: '1.5',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <code>{codeContent.join('\n')}</code>
          </pre>
        </Box>
      );
      codeContent = [];
      codeLanguage = '';
    }
  };

  const flushList = () => {
    if (listItems.length > 0) {
      const ListTag = listType;
      elements.push(
        <ListTag
          key={`list-${elements.length}`}
          style={{
            paddingLeft: '24px',
            margin: '8px 0 16px 0',
          }}
        >
          {listItems.map((item, idx) => (
            <li key={idx} style={{ marginBottom: '4px' }}>
              <Text as="span" variant="bodyMd">
                {item}
              </Text>
            </li>
          ))}
        </ListTag>
      );
      listItems = [];
      inList = false;
    }
  };

  const flushTable = () => {
    if (tableRows.length > 0 || tableHeader.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} style={{ overflowX: 'auto' }}>
          <Box
            borderRadius="200"
            borderColor="border"
            borderWidth="025"
          >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
            }}
          >
            {tableHeader.length > 0 && (
              <thead>
                <tr>
                  {tableHeader.map((cell, idx) => (
                    <th
                      key={idx}
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '1px solid var(--p-color-border)',
                        backgroundColor: 'var(--p-color-bg-surface-secondary)',
                        fontWeight: 600,
                      }}
                    >
                      {cell.trim()}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {tableRows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row.map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      style={{
                        padding: '12px',
                        borderBottom:
                          rowIdx < tableRows.length - 1
                            ? '1px solid var(--p-color-border)'
                            : 'none',
                      }}
                    >
                      {parseInlineFormatting(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        </div>
      );
      tableRows = [];
      tableHeader = [];
      inTable = false;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Handle code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        flushCodeBlock();
        inCodeBlock = false;
      } else {
        flushList();
        flushTable();
        inCodeBlock = true;
        codeLanguage = line.slice(3).trim();
      }
      continue;
    }

    if (inCodeBlock) {
      codeContent.push(line);
      continue;
    }

    // Check for visual markers
    const visualMarker = parseVisualMarker(line);
    if (visualMarker) {
      flushList();
      flushTable();

      if (visualMarker.type === 'illustration') {
        if (isIllustrationName(visualMarker.name)) {
          elements.push(
            <Box key={`visual-${elements.length}`} paddingBlock="400">
              <HelpIllustrationEmbed
                name={visualMarker.name}
                caption={visualMarker.caption}
              />
            </Box>
          );
        } else {
          // Unknown illustration - show placeholder
          elements.push(
            <Box
              key={`visual-${elements.length}`}
              padding="400"
              background="bg-surface-secondary"
              borderRadius="200"
            >
              <Text as="p" tone="subdued">
                [Illustration: {visualMarker.name}]
              </Text>
            </Box>
          );
        }
      } else if (visualMarker.type === 'chart') {
        if (isChartName(visualMarker.name)) {
          elements.push(
            <Box key={`visual-${elements.length}`} paddingBlock="400">
              <HelpChartEmbed
                name={visualMarker.name}
                caption={visualMarker.caption}
              />
            </Box>
          );
        } else {
          // Unknown chart - show placeholder
          elements.push(
            <Box
              key={`visual-${elements.length}`}
              padding="400"
              background="bg-surface-secondary"
              borderRadius="200"
            >
              <Text as="p" tone="subdued">
                [Chart: {visualMarker.name}]
              </Text>
            </Box>
          );
        }
      }
      continue;
    }

    // Handle tables
    if (line.startsWith('|') && line.endsWith('|')) {
      flushList();
      const cells = line
        .slice(1, -1)
        .split('|')
        .map((c) => c.trim());

      // Check if this is a separator row (---|---|---)
      if (cells.every((c) => /^[-:]+$/.test(c))) {
        continue; // Skip separator row
      }

      if (!inTable) {
        inTable = true;
        tableHeader = cells;
      } else {
        tableRows.push(cells);
      }
      continue;
    } else if (inTable) {
      flushTable();
    }

    // Handle headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushList();
      flushTable();
      const level = headingMatch[1].length;
      const headingText = headingMatch[2];
      const variant =
        level === 1
          ? 'headingXl'
          : level === 2
            ? 'headingLg'
            : level === 3
              ? 'headingMd'
              : 'headingSm';
      elements.push(
        <Box key={`heading-${elements.length}`} paddingBlockStart={level === 1 ? '0' : '400'}>
          <Text as={`h${level}` as any} variant={variant}>
            {parseInlineFormatting(headingText)}
          </Text>
        </Box>
      );
      continue;
    }

    // Handle horizontal rules
    if (/^---+$/.test(line.trim())) {
      flushList();
      flushTable();
      elements.push(
        <Box key={`hr-${elements.length}`} paddingBlock="400">
          <div
            style={{
              borderTop: '1px solid var(--p-color-border)',
            }}
          />
        </Box>
      );
      continue;
    }

    // Handle unordered lists
    if (line.match(/^\s*[-*]\s+/)) {
      if (!inList || listType !== 'ul') {
        flushList();
        inList = true;
        listType = 'ul';
      }
      const itemText = line.replace(/^\s*[-*]\s+/, '');
      listItems.push(parseInlineFormatting(itemText));
      continue;
    }

    // Handle ordered lists
    if (line.match(/^\s*\d+\.\s+/)) {
      if (!inList || listType !== 'ol') {
        flushList();
        inList = true;
        listType = 'ol';
      }
      const itemText = line.replace(/^\s*\d+\.\s+/, '');
      listItems.push(parseInlineFormatting(itemText));
      continue;
    }

    // End list if non-list line
    if (inList && line.trim() !== '' && !line.match(/^\s+/)) {
      flushList();
    }

    // Handle empty lines
    if (line.trim() === '') {
      flushList();
      continue;
    }

    // Handle blockquotes
    if (line.startsWith('>')) {
      flushList();
      flushTable();
      const quoteText = line.replace(/^>\s*/, '');
      elements.push(
        <Box
          key={`quote-${elements.length}`}
          paddingInlineStart="400"
          borderInlineStartWidth="025"
          borderColor="border"
        >
          <Text tone="subdued" as="p">
            {parseInlineFormatting(quoteText)}
          </Text>
        </Box>
      );
      continue;
    }

    // Regular paragraph
    flushList();
    flushTable();
    if (line.trim()) {
      elements.push(
        <Text key={`p-${elements.length}`} as="p" variant="bodyMd">
          {parseInlineFormatting(line)}
        </Text>
      );
    }
  }

  // Flush any remaining content
  flushCodeBlock();
  flushList();
  flushTable();

  return (
    <BlockStack gap="300">
      {elements}
    </BlockStack>
  );
}

export default MarkdownWithVisuals;
